import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';

const faqs = [
  { question: 'How long does it take to see measurable results?', answer: 'Most clients start seeing significant results within 30-60 days of campaign launch. Sustainable, compounding growth is our focus — many clients see 2-3x growth within the first quarter.' },
  { question: 'Do you offer customized packages for different budgets?', answer: 'Absolutely! We create fully customized strategies and packages based on your specific goals, budget, and industry. Whether you are a startup or an enterprise, we have scalable solutions for maximum ROI at every investment level.' },
  { question: 'How do you measure and report success?', answer: 'We use comprehensive analytics including ROI, conversion rates, customer acquisition costs, engagement metrics, website traffic, and revenue attribution. You receive detailed monthly reports with transparent metrics and strategic recommendations.' },
  { question: 'What kind of ongoing support do you provide?', answer: 'We offer premium 24/7 dedicated support. You will have a dedicated account manager, regular strategy sessions, real-time performance monitoring, and immediate response to any concerns.' },
];

function FAQItem({ faq, index, isOpen, onClick }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.01 }} className="relative group">
      <div className={`relative bg-gradient-to-br from-gray-50/90 dark:from-gray-900/90 to-white/90 dark:to-black/90 rounded-2xl border transition-all duration-500 backdrop-blur-md overflow-hidden ${isOpen ? 'border-cyan-500/40' : 'border-gray-200 dark:border-gray-800 group-hover:border-gray-300 dark:border-gray-700'}`}>
        <button onClick={onClick}
          className="w-full px-8 py-7 flex items-center justify-between text-left hover:bg-white/5 transition-colors">
          <span className="text-xl font-bold text-gray-900 dark:text-white pr-8 leading-relaxed">{faq.question}</span>
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.4, type: 'spring' }} className="flex-shrink-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-gradient-to-r from-cyan-500 to-blue-600' : 'bg-gray-200 dark:bg-gray-800'}`}>
              <FaChevronDown className="text-gray-900 dark:text-white text-lg" />
            </div>
          </motion.div>
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }}>
              <div className="px-8 pb-8 text-gray-600 dark:text-gray-400 leading-relaxed text-lg border-t border-gray-200 dark:border-gray-800 pt-6">
                {faq.answer}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section id="faq" className="py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} className="text-center mb-20">
          <motion.span whileHover={{ scale: 1.05 }}
            className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-500/10 to-orange-500/10 border border-cyan-500/20 rounded-full text-sm font-semibold text-cyan-400 mb-6 backdrop-blur-sm">
            FAQ
          </motion.span>
          <h2 className="text-5xl md:text-7xl font-black mb-6">
            Got <span className="bg-gradient-to-r from-cyan-400 to-orange-500 bg-clip-text text-transparent">Questions?</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">We've got the answers you need</p>
        </motion.div>
        <div className="space-y-6">
          {faqs.map((f, i) => (
            <FAQItem key={i} faq={f} index={i} isOpen={openIndex === i} onClick={() => setOpenIndex(openIndex === i ? null : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
